import {
  Component,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  AfterViewInit,
  signal,
  inject,
} from '@angular/core'
import { ActivatedRoute, Params, Router } from '@angular/router'
import {
  DataBindingDirective,
  DataStateChangeEvent,
} from '@progress/kendo-angular-grid'
import { State } from '@progress/kendo-data-query'
import { map, Observable, skip } from 'rxjs'

import {
  Enums,
  Environment,
  ExpandedProfile,
  Hunt,
  HUNT_MAX,
  HuntTemplate,
  HuntTemplateWithCount,
  Profile,
} from '@cyborg-monorepo/hunter/common'
import { DialogRef, DialogService, SnackbarService } from '@hyperkit'

import { DateRangeFilterComponent } from '../../../shared/components/custom-filters/date-range-filter.component'
import { DropDownFilterComponent } from '../../../shared/components/custom-filters/dropdown-filter.component'
import { MultiselectEnvironmentFilterComponent } from '../../../shared/components/custom-filters/multiselect-environment-filter.component'
import { MultiselectTagFilterComponent } from '../../../shared/components/custom-filters/multiselect-tag-filter.component'
import { NewHuntDialogComponent } from '../../../shared/components/dialogs/new-hunt-dialog/new-hunt-dialog.component'
import { NewTemplateDialogComponent } from '../../../shared/components/dialogs/new-template-dialog/new-template-dialog.component'
import { CompanyService } from '../../../shared/services/company.service'
import { EnvironmentService } from '../../../shared/services/environment.service'
import { HuntTemplateService } from '../../../shared/services/hunt-template.service'
import { ArchiveTemplateDialogComponent } from '../archive-template-dialog/archive-template-dialog.component'
import { HuntUrlService } from '../hunt-list/hunt-url-service'

import { HuntTemplateStateService } from './hunt-template-state-service'

@Component({
  standalone: false,
  templateUrl: './hunt-template-list.component.html',
  styleUrls: ['hunt-template-list.component.scss'],
})
export class HuntTemplateListComponent implements OnInit, AfterViewInit {
  private dialogService = inject(DialogService)
  private companyService = inject(CompanyService)
  router = inject(Router)
  huntUrlService = inject(HuntUrlService)
  huntTemplateStateService = inject(HuntTemplateStateService)
  environmentService = inject(EnvironmentService)
  private activatedRoute = inject(ActivatedRoute)
  private huntTemplateService = inject(HuntTemplateService)

  @ViewChild(DataBindingDirective) public dataBinding: DataBindingDirective
  @ViewChildren('template_dropdown')
  public dropdowns: QueryList<DropDownFilterComponent>
  @ViewChild('template_multiselect')
  public multiselect: MultiselectTagFilterComponent
  @ViewChildren('template_datepicker')
  public datepickers: QueryList<DateRangeFilterComponent>
  @ViewChildren('template_env_multiselect')
  public envMultiselects: QueryList<MultiselectEnvironmentFilterComponent>
  public huntTemplates: Array<HuntTemplate>
  public priority = Enums.PriorityEnum
  public status = Enums.StatusEnum
  public outcome = Enums.OutcomeEnum
  public users$: Observable<Array<Profile & ExpandedProfile>>
  public environments: Environment[]
  public readonly snack = inject(SnackbarService)

  public priorityFilters: Array<{
    text: string
    value: Enums.PriorityEnum
    class: string
  }> = [
    { text: 'Low', value: Enums.PriorityEnum.LOW, class: 'k-color-success' },
    {
      text: 'Medium',
      value: Enums.PriorityEnum.MEDIUM,
      class: 'k-color-warning',
    },
    { text: 'High', value: Enums.PriorityEnum.HIGH, class: 'k-color-error' },
  ]

  public huntTemplateState: State = {
    skip: 0,
    take: 25,
    sort: [],
    filter: null,
  }

  public ngOnInit(): void {
    this.environmentService.getAllEnvironments().subscribe((environments) => {
      this.environments = environments
    })

    this.users$ = this.companyService.getUsers().pipe(
      map((users: Array<Profile>) => {
        return users.map((user: Profile) => {
          return {
            ...user,
            ...{ fullName: user.firstName + ' ' + user.lastName },
          }
        })
      }),
    )

    this.huntTemplateStateService.huntTemplateState
      .pipe(skip(1))
      .subscribe((state: State) => {
        this.huntUrlService.gotoUrl(state)
      })
  }

  public newHunt(template: HuntTemplate): void {
    const dialogRef: DialogRef = this.dialogService.open(
      NewHuntDialogComponent,
      { data: { huntPackages: template.huntPackages, huntTemplate: template } },
    )

    dialogRef.afterClosed().subscribe((hunt: Hunt) => {
      if (hunt) {
        this.router.navigate(['hunt', hunt.id, 'workbench'])
      }
    })
  }

  public addToNewTemplate(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      NewTemplateDialogComponent,
    )
    dialogRef.afterClosed().subscribe((result: HuntTemplate) => {
      if (result) this.router.navigate(['hunt', 'hunt-template', result.id])
    })
  }

  public ngAfterViewInit(): void {
    if (Object.keys(this.activatedRoute.snapshot.queryParams).length) {
      this.populateFromUrl(this.activatedRoute.snapshot.queryParams)
    } else {
      this.huntUrlService.gotoUrl(
        this.huntTemplateStateService.huntTemplateState.value,
      )
    }
  }

  public isHuntDisabled(template: HuntTemplateWithCount): boolean {
    return (
      template.huntPackages.length == 0 ||
      (template.huntCount && template.huntCount >= HUNT_MAX)
    )
  }

  public archiveTemplate(huntTemplate: HuntTemplate): void {
    const dialogRef: DialogRef = this.dialogService.open(
      ArchiveTemplateDialogComponent,
      { data: huntTemplate },
    )

    dialogRef.afterClosed().subscribe((huntTemplate: HuntTemplate) => {
      this.huntTemplateStateService.updateState()
      if (huntTemplate) {
        this.snack.show(`Archived ${huntTemplate.name}`, 5000)
      }
    })
  }

  public unarchiveTemplate(huntTemplate: HuntTemplate): void {
    this.huntTemplateService
      .unarchiveTemplate(huntTemplate.id)
      .subscribe((huntTemplate: HuntTemplate) => {
        this.huntTemplateStateService.updateState()
        this.snack.show(`Removed ${huntTemplate.name} from archive`, 5000)
      })
  }

  public toggleArchivedTemplates(): void {
    this.huntTemplateStateService.updateState({
      archived: !this.huntTemplateStateService.huntTemplateState.value.archived,
    })
  }

  public getTooltipText(template: HuntTemplateWithCount): string {
    let toolTip = ''
    if (template.huntPackages.length === 0)
      toolTip = `At least one hunt package is needed to create a hunt.`
    if (template.huntCount && template.huntCount >= HUNT_MAX)
      toolTip = `You have reached the maximum number of hunts (${HUNT_MAX}) for this template.`
    return toolTip
  }

  private populateFromUrl(queryParams: Params): void {
    let searchState: State =
      this.huntUrlService.searchStateFromString(queryParams)
    if (!searchState) {
      searchState = this.huntTemplateStateService.huntTemplateState.value
    }
    this.huntTemplateStateService.setState(searchState)
  }

  public onHuntTemplatePageChange(e: DataStateChangeEvent): void {
    if (!e.sort[0].dir) e.sort = [{ field: 'name', dir: 'asc' }]

    this.huntTemplateStateService.updateState(e)
  }

  public clearAllFilters(): void {
    this.dropdowns.forEach((DropDownFilterComponent) => {
      DropDownFilterComponent.resetDefault()
    })

    this.datepickers.forEach((MultiselectTagFilterComponent) => {
      MultiselectTagFilterComponent.resetDefault()
    })

    this.multiselect.resetDefault()
    this.huntTemplateStateService.reset()
  }
}
